import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverStoryArticle() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "story-of-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "history-overview": "Historical Overview",
    "stanley-park": "Stanley Park",
    "gastown": "Gastown",
    "granville-island": "Granville Island",
    "moa": "MOA (Museum of Anthropology)",
    "chinese-garden": "Dr. Sun Yat-Sen Classical Chinese Garden",
    "capilano": "Capilano Suspension Bridge Park",
    "museum-of-vancouver": "Museum of Vancouver",
    "canada-place": "Canada Place",
    "grouse-mountain": "Grouse Mountain",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver (Official Tourism Board)",
    },
    {
      url: "https://www.hellobc.com",
      label: "HelloBC – Official BC Tourism Site",
    },
    {
      url: "https://translink.ca",
      label: "TransLink Public Transit",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" corresponds to the section ID in the article content.
  // Coordinates must match precisely.
  const locations = [
    {
      id: "stanley-park",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park",
    },
    {
      id: "gastown",
      lat: 49.28470,
      lng: -123.11100,
      popupText: "Gastown Historic District",
    },
    {
      id: "granville-island",
      lat: 49.27080,
      lng: -123.13420,
      popupText: "Granville Island",
    },
    {
      id: "moa",
      lat: 49.26940,
      lng: -123.25960,
      popupText: "Museum of Anthropology at UBC",
    },
    {
      id: "chinese-garden",
      lat: 49.27970,
      lng: -123.10650,
      popupText: "Dr. Sun Yat-Sen Classical Chinese Garden",
    },
    {
      id: "capilano",
      lat: 49.34290,
      lng: -123.11490,
      popupText: "Capilano Suspension Bridge Park",
    },
    {
      id: "museum-of-vancouver",
      lat: 49.27610,
      lng: -123.14390,
      popupText: "Museum of Vancouver",
    },
    {
      id: "canada-place",
      lat: 49.28880,
      lng: -123.11130,
      popupText: "Canada Place",
    },
    {
      id: "grouse-mountain",
      lat: 49.37940,
      lng: -123.08240,
      popupText: "Grouse Mountain",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Story of Vancouver</title>
        <meta
          name="description"
          content="Explore the rich history and vibrant locations that define Vancouver, from its Indigenous roots to modern urban culture."
        />
        <meta
          name="keywords"
          content="Vancouver, travel, history, Stanley Park, Gastown, Granville Island, MOA, Capilano Suspension Bridge"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/story-of-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Story of Vancouver"
        />
        <meta
          property="og:description"
          content="A comprehensive guide to Vancouver’s history, key landmarks, local regulations, and tips for travelers."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/story-of-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/story-of-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Story of Vancouver"
        />
        <meta
          name="twitter:description"
          content="A comprehensive guide to Vancouver’s history, key landmarks, local regulations, and tips for travelers."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/story-of-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Panoramic view of Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a bustling coastal city in British Columbia,
              renowned for its spectacular natural backdrop and dynamic
              urban setting. From its Indigenous heritage to its role as
              a modern gateway to the Pacific, Vancouver’s story is deeply
              intertwined with diverse cultures, global trade, and a strong
              focus on sustainability. This guide takes you through the
              city’s history, key locations to explore, useful tips,
              and local regulations to keep in mind during your visit.
            </p>

            {/* SECTION: Historical Overview */}
            <h2 id="history-overview">Historical Overview</h2>
            <p>
              Vancouver’s origins trace back thousands of years to the
              Indigenous peoples of the Pacific Northwest Coast. The
              Musqueam, Squamish, and Tsleil-Waututh Nations have inhabited
              these lands since time immemorial, building rich cultural
              traditions and vibrant communities. Following European
              contact in the late 18th century, colonial settlements
              emerged around Burrard Inlet. The area rapidly developed
              with the establishment of Gastown in 1867, fueled by the
              sawmill industry and eventually spurred on by the arrival
              of the Canadian Pacific Railway in 1887.
            </p>
            <p>
              Throughout the 20th century, Vancouver evolved into a
              cosmopolitan center, hosting events like Expo 86 and the
              2010 Winter Olympics. Today, it stands as one of Canada’s
              most diverse and sustainable urban areas—known for its
              striking natural vistas, progressive urban planning,
              and cultural richness.
            </p>

            {/* SECTION: Stanley Park */}
            <h2 id="stanley-park" ref={(el) => setSpotRef("stanley-park", el)} style={{ scrollMarginTop: "80px" }}>
              Stanley Park
              <Marker coordY={49.30000} coordX={-123.14000} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              Stanley Park (49.30000, -123.14000) is Vancouver’s oldest and
              largest urban park. Encompassing over 400 hectares of
              rainforest, scenic seawall, and cultural landmarks, it stands
              as a testament to the city’s commitment to preserving green
              spaces. Established in 1888, the park offers lush walking
              trails, beaches, historic totem poles at Brockton Point, and
              panoramic views of the North Shore Mountains. Whether you’re
              cycling the Seawall or exploring the Vancouver Aquarium
              within the park, Stanley Park offers a glimpse into
              Vancouver’s harmonious balance between nature and city life.
            </p>

            {/* SECTION: Gastown */}
            <h2 id="gastown" ref={(el) => setSpotRef("gastown", el)} style={{ scrollMarginTop: "80px" }}>
              Gastown
              <Marker coordY={49.28470} coordX={-123.11100} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              Gastown (49.28470, -123.11100) is the historical heart of
              Vancouver. Founded by the talkative “Gassy Jack” Deighton
              in 1867, it evolved around the area’s first tavern. Today,
              its cobblestone streets, Victorian architecture, and famous
              steam clock draw visitors eager to experience a taste of
              Vancouver’s early roots. Gastown seamlessly merges heritage
              charm with modern boutiques, galleries, and eateries.  
            </p>

            {/* SECTION: Granville Island */}
            <h2 id="granville-island" ref={(el) => setSpotRef("granville-island", el)} style={{ scrollMarginTop: "80px" }}>
              Granville Island
              <Marker coordY={49.27080} coordX={-123.13420} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              Granville Island (49.27080, -123.13420) was once an industrial
              site filled with warehouses and factories. Redeveloped in the
              1970s into a bustling cultural and culinary hub, it’s now
              home to the Granville Island Public Market, artisan workshops,
              theatres, restaurants, and galleries. Visitors can sample
              fresh produce, unique local crafts, or simply enjoy the
              waterfront views. Easily accessible by foot, car, or small
              ferry rides, Granville Island stands as a testament to
              Vancouver’s creative transformation of its industrial past.
            </p>

            {/* SECTION: MOA */}
            <h2 id="moa" ref={(el) => setSpotRef("moa", el)} style={{ scrollMarginTop: "80px" }}>
              MOA (Museum of Anthropology)
              <Marker coordY={49.26940} coordX={-123.25960} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              The Museum of Anthropology (49.26940, -123.25960) at the
              University of British Columbia is an architectural marvel
              dedicated to the art and cultures of Indigenous peoples,
              particularly those of the Pacific Northwest Coast.
              Housing an extensive collection of totem poles, carvings,
              masks, and global artifacts, the MOA provides deep insights
              into First Nations heritage. From Bill Reid’s iconic “The
              Raven and the First Men” sculpture to rotating international
              exhibits, the MOA fosters an appreciation for both historical
              and contemporary cultural expressions.
            </p>

            {/* SECTION: Dr. Sun Yat-Sen Classical Chinese Garden */}
            <h2
              id="chinese-garden"
              ref={(el) => setSpotRef("chinese-garden", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Dr. Sun Yat-Sen Classical Chinese Garden
              <Marker coordY={49.27970} coordX={-123.10650} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              Tucked away in Vancouver’s Chinatown, the Dr. Sun Yat-Sen
              Classical Chinese Garden (49.27970, -123.10650) was built
              in 1986 by master artisans from Suzhou, China. Modeled after
              Ming Dynasty gardens, it offers winding pathways, koi-filled
              ponds, and traditional pavilions. The garden is a cultural
              bridge between Vancouver’s historic Chinese community and
              the modern cityscape. Admission fees apply for the main
              garden, while a neighboring park section remains open to
              the public free of charge.
            </p>

            {/* SECTION: Capilano */}
            <h2
              id="capilano"
              ref={(el) => setSpotRef("capilano", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Capilano Suspension Bridge Park
              <Marker coordY={49.34290} coordX={-123.11490} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              Capilano Suspension Bridge Park (49.34290, -123.11490) in
              North Vancouver is a thrilling destination where visitors
              cross a 140-meter-long bridge suspended 70 meters above
              the Capilano River. Originally built in 1889, it has been
              reinforced over time to safely carry thousands of visitors
              each year. The park also features the Treetops Adventure
              and the Cliffwalk, along with Indigenous totem poles that
              celebrate local First Nations art and history.
            </p>

            {/* SECTION: Museum of Vancouver */}
            <h2
              id="museum-of-vancouver"
              ref={(el) => setSpotRef("museum-of-vancouver", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Museum of Vancouver
              <Marker coordY={49.27610} coordX={-123.14390} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              The Museum of Vancouver (49.27610, -123.14390) in Vanier Park
              showcases the city’s civic history, from its early Indigenous
              roots through its economic booms and social transformations.
              Visitors can explore exhibits detailing 1950s neon signs,
              the city’s evolution in pop culture, and significant moments
              like Expo 86. Adjacent to the H.R. MacMillan Space Centre, 
              this museum offers a fascinating look at how Vancouver has
              grown and changed over the decades.
            </p>

            {/* SECTION: Canada Place */}
            <h2
              id="canada-place"
              ref={(el) => setSpotRef("canada-place", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Canada Place
              <Marker coordY={49.28880} coordX={-123.11130} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              Canada Place (49.28880, -123.11130) is an iconic waterfront
              landmark recognized by its distinctive sail-like roof.
              Originally constructed for Expo 86, it now houses a cruise
              ship terminal, a convention center, and the FlyOver Canada
              attraction. Walking along its promenade offers views of the
              Burrard Inlet, the North Shore Mountains, and Stanley Park
              to the west.
            </p>

            {/* SECTION: Grouse Mountain */}
            <h2
              id="grouse-mountain"
              ref={(el) => setSpotRef("grouse-mountain", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Grouse Mountain
              <Marker coordY={49.37940} coordX={-123.08240} mobileMapRef={mobileMapRef} mapRef={mapRef} />
            </h2>
            <p>
              Known as the “Peak of Vancouver,” Grouse Mountain (49.37940,
              -123.08240) provides year-round recreation. Visitors can
              take the Skyride aerial tram for panoramic city views, enjoy
              hiking in summer, or ski and snowboard in winter. The
              mountaintop also features a wildlife refuge for rescued
              grizzly bears and a thrilling zipline across the scenic
              canyon. Whether for sunrise hikes or night skiing, Grouse
              Mountain offers a glimpse of Vancouver’s adventure-driven
              spirit.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              <strong>Parks and Wildlife Etiquette:</strong> Feeding wildlife
              is strictly prohibited in Vancouver’s parks. Alcohol
              consumption is only allowed in designated areas, and smoking
              (including cannabis) is banned in public parks and beaches.
              <br />
              <strong>Indigenous and Cultural Respect:</strong> When visiting
              sites with Indigenous significance, respect posted guidelines
              regarding photography or touching artifacts. 
              <br />
              <strong>Transportation:</strong> Helmets are mandatory for all
              cyclists; e-scooters are allowed only in certain pilot areas.
              <br />
              <strong>General Conduct:</strong> Vancouver is quite safe, but
              remain alert for petty theft. Avoid carrying open liquor in
              public unless you’re in a legal consumption zone.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. When is the best time to visit Vancouver?</h3>
              <p>
                Summer (June–September) offers mild weather and outdoor
                activities. Shoulder seasons like spring and fall can be
                less crowded, though the weather can be unpredictable.
              </p>

              <h3>2. How do I get around the city?</h3>
              <p>
                Vancouver’s public transit (buses, SkyTrain, SeaBus) is
                efficient and often negates the need for a car. Biking
                and walking are also popular, especially around the
                downtown core and nearby attractions.
              </p>

              <h3>3. Is Vancouver safe?</h3>
              <p>
                Yes, Vancouver is generally very safe. Like any large city,
                you may encounter petty theft in crowded areas. Gastown
                and Chinatown border the Downtown Eastside, where you’ll see
                homelessness and people in vulnerable conditions. Exercise
                basic caution, particularly at night.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver seamlessly blends spectacular natural scenery
              with multicultural urban life. Whether you’re strolling
              through Gastown’s historic streets, exploring Indigenous
              art at the MOA, or enjoying the views atop Grouse Mountain,
              this city offers endless ways to delve into its past and
              present. Embrace its progressive spirit, respect local
              regulations, and you’ll create lasting memories of Vancouver’s
              vibrant story.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}